import { CaseStage } from '../types';

export const caseStages: CaseStage[] = [
  // Pre-Case / Lead Management
  {
    id: 'new-lead',
    category: 'Pre-Case',
    name: 'New Lead',
    description: 'Inquiry received but not yet reviewed',
    order: 1
  },
  {
    id: 'pending-intake',
    category: 'Pre-Case',
    name: 'Pending Intake',
    description: 'Awaiting client response or further details',
    order: 2
  },
  {
    id: 'conflict-check',
    category: 'Pre-Case',
    name: 'Conflict Check',
    description: 'Reviewing potential conflicts of interest',
    order: 3
  },
  {
    id: 'intake-completed',
    category: 'Pre-Case',
    name: 'Intake Completed',
    description: 'Client intake form completed, ready for review',
    order: 4
  },
  {
    id: 'awaiting-retainer',
    category: 'Pre-Case',
    name: 'Awaiting Retainer',
    description: 'Retainer agreement sent, pending signature/payment',
    order: 5
  },
  {
    id: 'retainer-signed',
    category: 'Pre-Case',
    name: 'Retainer Signed',
    description: 'Client has signed the agreement and made payment',
    order: 6
  },

  // Case Initiation
  {
    id: 'case-opened',
    category: 'Case Initiation',
    name: 'Case Opened',
    description: 'Case formally accepted and assigned to an attorney',
    order: 7
  },
  {
    id: 'initial-review',
    category: 'Case Initiation',
    name: 'Initial Review',
    description: 'Gathering preliminary case details and assessing viability',
    order: 8
  },
  {
    id: 'awaiting-documents',
    category: 'Case Initiation',
    name: 'Awaiting Client Documents',
    description: 'Request sent for necessary documents',
    order: 9
  },
  {
    id: 'documents-received',
    category: 'Case Initiation',
    name: 'Documents Received',
    description: 'Initial set of client documents reviewed',
    order: 10
  },
  {
    id: 'investigation',
    category: 'Case Initiation',
    name: 'Investigation Ongoing',
    description: 'Conducting background checks, witness interviews, and research',
    order: 11
  },

  // Pre-Litigation / Discovery
  {
    id: 'demand-letter',
    category: 'Pre-Litigation',
    name: 'Demand Letter Sent',
    description: 'Official demand for settlement or resolution issued',
    order: 12
  },
  {
    id: 'negotiation',
    category: 'Pre-Litigation',
    name: 'Negotiation in Progress',
    description: 'Settlement discussions with the opposing party',
    order: 13
  },
  {
    id: 'settlement-offer-received',
    category: 'Pre-Litigation',
    name: 'Settlement Offer Received',
    description: 'Offer received from opposing party, under review',
    order: 14
  },
  {
    id: 'settlement-offer-rejected',
    category: 'Pre-Litigation',
    name: 'Settlement Offer Rejected',
    description: 'Client/attorney rejected the settlement',
    order: 15
  },
  {
    id: 'mediation-scheduled',
    category: 'Pre-Litigation',
    name: 'Mediation Scheduled',
    description: 'Mediation session planned with a neutral party',
    order: 16
  },
  {
    id: 'mediation-in-progress',
    category: 'Pre-Litigation',
    name: 'Mediation in Progress',
    description: 'Active discussions for resolution',
    order: 17
  },
  {
    id: 'mediation-completed',
    category: 'Pre-Litigation',
    name: 'Mediation Completed',
    description: 'Mediation concluded, awaiting next steps',
    order: 18
  },

  // Litigation
  {
    id: 'lawsuit-filed',
    category: 'Litigation',
    name: 'Lawsuit Filed',
    description: 'Formal complaint filed in court',
    order: 19
  },
  {
    id: 'service-pending',
    category: 'Litigation',
    name: 'Service of Process Pending',
    description: 'Waiting for opposing party to be served',
    order: 20
  },
  {
    id: 'awaiting-response',
    category: 'Litigation',
    name: 'Awaiting Response from Defendant',
    description: 'Waiting for the other party\'s reply',
    order: 21
  },
  {
    id: 'discovery-phase',
    category: 'Litigation',
    name: 'Discovery Phase',
    description: 'Exchanging evidence, depositions, interrogatories',
    order: 22
  },
  {
    id: 'motion-practice',
    category: 'Litigation',
    name: 'Motion Practice',
    description: 'Filing/reviewing motions (dismiss, summary judgment, etc.)',
    order: 23
  },
  {
    id: 'pre-trial-conference',
    category: 'Litigation',
    name: 'Pre-Trial Conference',
    description: 'Meeting with the court to discuss trial logistics',
    order: 24
  },
  {
    id: 'trial-scheduled',
    category: 'Litigation',
    name: 'Trial Scheduled',
    description: 'Court has set a trial date',
    order: 25
  },
  {
    id: 'trial-preparation',
    category: 'Litigation',
    name: 'Trial Preparation',
    description: 'Finalizing trial strategy, exhibits, and witness prep',
    order: 26
  },
  {
    id: 'trial-in-progress',
    category: 'Litigation',
    name: 'Trial in Progress',
    description: 'Case is currently being litigated in court',
    order: 27
  },

  // Judgment & Appeals
  {
    id: 'verdict-pending',
    category: 'Judgment & Appeals',
    name: 'Verdict Pending',
    description: 'Awaiting judge/jury decision',
    order: 28
  },
  {
    id: 'judgment-issued',
    category: 'Judgment & Appeals',
    name: 'Judgment Issued',
    description: 'Court has ruled on the case',
    order: 29
  },
  {
    id: 'post-trial-motions',
    category: 'Judgment & Appeals',
    name: 'Post-Trial Motions',
    description: 'Filing motions (e.g., appeal, reconsideration)',
    order: 30
  },
  {
    id: 'appeal-filed',
    category: 'Judgment & Appeals',
    name: 'Appeal Filed',
    description: 'Case moved to an appellate court for review',
    order: 31
  },
  {
    id: 'appeal-in-progress',
    category: 'Judgment & Appeals',
    name: 'Appeal in Progress',
    description: 'Higher court is reviewing the case',
    order: 32
  },
  {
    id: 'appeal-decided',
    category: 'Judgment & Appeals',
    name: 'Appeal Decided',
    description: 'Appeal ruling issued; case outcome finalized',
    order: 33
  },

  // Settlement & Disbursement
  {
    id: 'settlement-draft',
    category: 'Settlement',
    name: 'Settlement Agreement Drafted',
    description: 'Settlement terms being finalized',
    order: 34
  },
  {
    id: 'settlement-signed',
    category: 'Settlement',
    name: 'Settlement Agreement Signed',
    description: 'Client and opposing party have signed',
    order: 35
  },
  {
    id: 'awaiting-payment',
    category: 'Settlement',
    name: 'Awaiting Payment',
    description: 'Funds pending from defendant or insurer',
    order: 36
  },
  {
    id: 'payment-received',
    category: 'Settlement',
    name: 'Payment Received',
    description: 'Settlement funds received',
    order: 37
  },
  {
    id: 'disbursement-in-progress',
    category: 'Settlement',
    name: 'Disbursement in Progress',
    description: 'Allocating funds to attorneys, client, and expenses',
    order: 38
  },
  {
    id: 'disbursement-completed',
    category: 'Settlement',
    name: 'Disbursement Completed',
    description: 'All funds distributed; case closed',
    order: 39
  },

  // Case Closure
  {
    id: 'case-dismissed',
    category: 'Case Closure',
    name: 'Case Dismissed',
    description: 'Case voluntarily/involuntarily dismissed',
    order: 40
  },
  {
    id: 'closed-successful',
    category: 'Case Closure',
    name: 'Closed - Successful Outcome',
    description: 'Case resolved in favor of the client',
    order: 41
  },
  {
    id: 'closed-unsuccessful',
    category: 'Case Closure',
    name: 'Closed - Unsuccessful Outcome',
    description: 'Case did not result in a favorable decision',
    order: 42
  },
  {
    id: 'closed-withdrawn',
    category: 'Case Closure',
    name: 'Closed - Client Withdrew',
    description: 'Client terminated representation',
    order: 43
  },
  {
    id: 'closed-transferred',
    category: 'Case Closure',
    name: 'Closed - Transferred',
    description: 'Case handed off to another law firm',
    order: 44
  },
  {
    id: 'archived',
    category: 'Case Closure',
    name: 'Archived',
    description: 'Case stored for future reference',
    order: 45
  },
];